/* eslint-disable consistent-return */
/* eslint-disable no-nested-ternary */
import React, { useContext } from 'react';
import parse from 'html-react-parser';
import { makeStyles, createStyles, useTheme } from '@material-ui/core/styles';
import ClickAwayListener from '@material-ui/core/ClickAwayListener';
import { Tooltip } from '@material-ui/core';
import ListAltIcon from '@material-ui/icons/ListAlt';
import { PSO, PRN } from '@mummssoftware/utils/orders/orders-constants';
import {
  OrdersGlobalContext,
  PNGlobalContext,
} from '@mummssoftware/global-context';
import {
  IDG_MENU,
  MEDS_MENU,
  CPP_PLANS_MENU,
  CPF_PLANS_MENU,
  EA_MENU,
} from '@mummssoftware/utils/orders';
import { getTagMap } from './utils/utils';
import TagComponent from '../TagNote/TagComponent';
import {
  order,
  idg,
  pcpp,
  meas,
  pcpf,
  pcppt,
  mm,
  cpf,
  ea,
  mapInitial,
} from '../Context/constants';
import { Context } from '../../context';
import { ProgressNotesContext } from '../Context/ProgressNotesContext';
import { formatMMDDYY, formathhmm } from '../../utils/utils';
import {
  NoteWrapper,
  NoteContentDiv,
  TopNoteBorder,
  NoteBorder,
  LeftTopNoteBorder,
  RightTopNoteBorder,
  StyledText,
  StyledTagText,
  IconRightTopNoteBorder,
  OrderRightTopNoteBorder,
  StyledUl,
  TooltipDiv,
  Tag,
} from '../../components/layout/WrapperDiv';
import defaults from '../../translations/en.json';
import './utils/styles.css';

type NoteTagProps = {
  noteTag: mumms.NoteTagType | null;
};

const useStyles = makeStyles(({ palette }: any) =>
  createStyles({
    tooltip: {
      backgroundColor: palette.background.tooltip,
      // minWidth: (props) => props.containerWidth * 0.97,
      padding: '4px',
      margin: '0px',
      maxHeight: '250px',
      overflow: 'auto',
    },
    arrow: {
      color: palette.common.white,
    },
  }),
);

const TagDescription = ({ info, type }: mumms.TagDescriptionProps) => {
  const isPSO = type === PSO;
  // @note TagDescription only called on click
  const { ordersGlobalMap } = useContext(ProgressNotesContext);
  const noteInfoId = isPSO && info && info[0].entitykey;
  const psoNoteInfo =
    noteInfoId && ordersGlobalMap
      ? ordersGlobalMap[noteInfoId]
      : defaults.noRecords;
  let items: any | null;
  if (info) {
    if (isPSO) {
      items = psoNoteInfo;
    } else if (info.length) {
      items = info.map((i: any) => i.entityname || i.entitykey);
    }
  }

  const getUI = () => {
    if (items) {
      if (isPSO) {
        return <TooltipDiv id="tag-description">{parse(items)}</TooltipDiv>;
      }
      return (
        <StyledUl id="tag-desc">
          {items.map((i: string) => (
            <li key={i}>{i}</li>
          ))}
        </StyledUl>
      );
    }
    return <></>;
  };

  return getUI();
};

const getIcon = (type: mumms.TagType | undefined) => {
  if (type) {
    switch (type) {
      case PSO:
        return <ListAltIcon />;
      default:
        return <StyledTagText>{mapInitial[type]}</StyledTagText>;
    }
  }
};

const NoteTag = ({ noteTag }: NoteTagProps) => {
  const type = noteTag?.type;
  const success = noteTag?.success;
  const info = noteTag?.info;
  const { palette } = useTheme() as any;
  const historyClasses = useStyles();
  // todo move to utils
  const showDesc =
    type === meas ||
    type === mm ||
    type === pcpp ||
    type === pcppt ||
    type === pcpf ||
    type === ea ||
    type === PSO;

  const [open, setOpen] = React.useState(false);
  const handleTooltipClose = () => {
    setOpen(false);
  };

  const handleTooltipOpen = () => {
    setOpen(true);
  };

  let color;
  switch (type) {
    case order:
      color = success ? palette.success.main : palette.error.main;
      break;
    case idg:
      color = palette.idg.light;
      break;
    case ea:
      color = palette.ea.light;
      break;
    case mm:
      color = palette.med.light;
      break;
    case meas:
      color = palette.meas.light;
      break;
    case pcpp:
      color = palette.pcpp.light;
      break;
    case pcpf:
      color = palette.pcpf.light;
      break;
    case cpf:
      color = palette.pcpf.light;
      break;
    case pcppt:
      color = palette.pcppt.light;
      break;
  }

  return noteTag ? (
    <>
      <ClickAwayListener onClickAway={handleTooltipClose}>
        <Tooltip
          arrow
          PopperProps={{
            disablePortal: true,
          }}
          classes={{
            tooltip: historyClasses.tooltip,
            arrow: historyClasses.arrow,
          }}
          interactive
          onClose={handleTooltipClose}
          open={open}
          disableFocusListener
          disableHoverListener
          disableTouchListener
          // containerWidth={200}
          title={
            <div id="tag-description">
              <TagDescription info={info} type={type} />
            </div>
          }
        >
          <Tag
            color={color}
            type={type}
            onClick={showDesc ? handleTooltipOpen : undefined}
          >
            {getIcon(type)}
          </Tag>
        </Tooltip>
      </ClickAwayListener>
    </>
  ) : (
    <Tag />
  );
};

type TopRightNoteProp = {
  tagTypes?: mumms.AllTagTypes | null;
};
// right order
const allNoteTags: mumms.TagType[] = [
  order,
  idg,
  ea,
  mm,
  pcpp,
  pcpf,
  pcppt,
  meas,
];

const TopRightNote = ({ tagTypes }: TopRightNoteProp) => {
  const getNoteTags = () => {
    // have the labels always in the same position
    if (tagTypes && Object.keys(tagTypes).length) {
      return allNoteTags.reduce((seed: any, tag: mumms.TagType) => {
        const tagType: mumms.NoteTagType | undefined = tagTypes[tag];
        if (tagType) {
          seed.push(<NoteTag noteTag={tagType} key={tagType.type} />);
        } else {
          seed.push(<NoteTag noteTag={null} key={tag} />);
        }
        return seed;
      }, []);
    }
    return null;
  };
  const getOrder = () => {
    const standingOrder: mumms.NoteTagType | null | undefined =
      tagTypes && tagTypes[PSO];

    if (standingOrder) {
      return <NoteTag noteTag={standingOrder} key={standingOrder.type} />;
    }
    return null;
  };

  return tagTypes ? (
    <RightTopNoteBorder id="right-top-note-border-tagging">
      <OrderRightTopNoteBorder id="order-top-note-border">
        {getOrder()}
      </OrderRightTopNoteBorder>
      <IconRightTopNoteBorder id="icon-top-note-border">
        {getNoteTags()}
      </IconRightTopNoteBorder>
    </RightTopNoteBorder>
  ) : null;
};

const TopLeftNote = ({ note }: BorderNoteProps) => (
  <LeftTopNoteBorder id="left-top-note-border">
    <StyledText>{formatMMDDYY(note)}</StyledText>
    <StyledText>|</StyledText>
    <StyledText>{formathhmm(note)}</StyledText>
    <StyledText>|</StyledText>
    <StyledText>
      {note.personname}{' '}
      {note.credentials === 'null' || note.credentials === null
        ? ''
        : note.credentials}
    </StyledText>
  </LeftTopNoteBorder>
);

type BorderNoteProps = {
  note: mumms.progressNotebyEntity | mumms.progressNote;
  smallWidget?: boolean;
  // InProgress indicates if this is the active one being edited
  inProgress?: boolean;

  tagEntity?: mumms.AllTagTypes | null;
  publishedNoteMenu?: mumms.IntroMenuType[] | null;
  readOnly?: boolean;
};

export const BorderNote = ({
  note,
  smallWidget,
  inProgress,
  tagEntity,
  readOnly,
}: BorderNoteProps) => {
  const { showFilter, patient } = useContext(Context);
  const { savedNote } = useContext(OrdersGlobalContext);
  const { noteStarted } = useContext(ProgressNotesContext);
  const { patientApprovalsGlobal } = useContext(PNGlobalContext);
  const patientApprovals = patientApprovalsGlobal || [];
  let allTags: mumms.AllTagTypes | null | undefined = null;
  let publishedNoteMenu: mumms.IntroMenuType[] | null = null;
  if (note.id) {
    if (inProgress) {
      allTags = getTagMap(note, patientApprovals);
    } else {
      allTags = tagEntity;
    }

    const hasEpisodes = patient?.episodes && patient.episodes.length;
    const isHospice =
      hasEpisodes &&
      patient.episodes[patient.episodes.length - 1].programId === -1;
    const isEATagged = !!(allTags && allTags[ea]);
    const eaMenuItems: mumms.IntroMenuType[] =
      isHospice && !isEATagged ? [EA_MENU] : [];

    publishedNoteMenu =
      allTags && allTags[idg]
        ? [MEDS_MENU, CPP_PLANS_MENU, CPF_PLANS_MENU, ...eaMenuItems]
        : [IDG_MENU, MEDS_MENU, CPP_PLANS_MENU, CPF_PLANS_MENU, ...eaMenuItems];
  }

  // Issue with having in Progress Border in Note from that code => menu closes
  // When clicking on Tag it creates a note => saved Note,
  // triggers Context and useContext(Context) creates a re-render of the Border
  // closing the menu

  // if saved Note cannot tag
  // if there is a note in progress, can't add tags to published note
  // if a note is being written
  // todo how to know a note is being written
  const disabled = !!savedNote || noteStarted || !!showFilter;
  const noTagging = inProgress || readOnly;

  return (
    <TopNoteBorder
      inProgress={inProgress}
      smallWidget={smallWidget}
      key={`border-${note.id}`}
      id="top-note-border"
    >
      {!noTagging && (
        <div id="tag-component">
          <TagComponent
            disabled={disabled}
            note={note as mumms.progressNotebyEntity}
            publishedNoteMenu={publishedNoteMenu}
          />
        </div>
      )}
      <NoteBorder key={`note-border-${note.id}`} id="note-border">
        <TopLeftNote note={note} />
        <TopRightNote tagTypes={allTags} />
      </NoteBorder>
    </TopNoteBorder>
  );
};

type NoteProps = {
  note: mumms.progressNotebyEntity | mumms.progressNote;
  smallWidget?: boolean;
  readOnly?: boolean;
  style?: any;
};

export const Note = ({ note, style, smallWidget, readOnly }: NoteProps) => {
  const { patientApprovalsGlobal } = useContext(PNGlobalContext);
  const patientApprovals = patientApprovalsGlobal || [];

  if (!note?.note) {
    return null;
  }
  let allTags: mumms.AllTagTypes | null = null;
  if (note.id && note.patientprogressnoteentitydto) {
    allTags = getTagMap(note, patientApprovals);
  }
  // todo ipad in pim view
  if (style) {
    style.top -= 40;
  }
  const noteContent = parse(note.note);

  return (
    <NoteWrapper key={note.id} id="note-wrapper" style={style}>
      <BorderNote
        note={note}
        smallWidget={smallWidget}
        tagEntity={allTags}
        readOnly={readOnly}
      />
      <NoteContentDiv key={note.id} id="note-content">
        <span>{noteContent}</span>
      </NoteContentDiv>
    </NoteWrapper>
  );
};
